import "./contact.css";

import React from "react";

import { CONTACT, CONTACT_DETAILS } from "../../data/content";
import { postRequest } from "../../utils/apiHandlers";
import Consultation from "../general/Consultation";
import PageHero from "../general/PageHero";
import PageMeta from "../general/PageMeta";

const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  location: "",
  subject: "",
  budgetEstimate: "",
  areas: [],
  message: "",
  website: "", //Honeypot
};

const Field = ({ label, isRequired = false, children }) => (
  <label className={"contact-field"}>
    <span className={"contact-label"}>
      {label}
      {isRequired ? <span aria-hidden={"true"}> *</span> : null}
    </span>
    {children}
  </label>
);

const Contact = () => {
  const [form, setForm] = React.useState(EMPTY);
  const [isSending, setIsSending] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [sentTo, setSentTo] = React.useState(null);
  const areasRef = React.useRef(null);

  const update = (prop) => (e) => setForm({ ...form, [prop]: e.target.value });

  const toggleArea = (area) => (e) => setForm({
    ...form,
    areas: e.target.checked ? [...form.areas, area] : form.areas.filter((a) => a !== area),
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");
    if (form.areas.length === 0) {
      setErrorText("Please choose at least one area.");
      areasRef.current.querySelector("input").focus();
      return;
    }
    setIsSending(true);
    try {
      await postRequest("/Contact/SendContactMessage", form);
      setSentTo(form.firstName);
      setForm(EMPTY);
    }
    catch (err) {
      setErrorText(err.message);
    }
    setIsSending(false);
  };

  return (
    <React.Fragment>
      <PageMeta title={"Contact"} />
      <PageHero />

      <section className={"contact"}>
        <div className={"contact-form-wrap"}>
          <h1 className={"contact-title"}>{CONTACT.TITLE}</h1>

          {sentTo !== null ? (
            <div
              className={"contact-success"}
              role={"status"}
            >
              <p>Thanks for your message{sentTo ? `, ${sentTo}` : ""}! Our team will be in touch soon.</p>
              <button
                type={"button"}
                className={"btn"}
                onClick={() => setSentTo(null)}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              className={"contact-form"}
              onSubmit={onSubmit}
            >
              <Field
                label={"First name"}
                isRequired
              >
                <input
                  type={"text"}
                  value={form.firstName}
                  onChange={update("firstName")}
                  required
                  maxLength={100}
                  autoComplete={"given-name"}
                />
              </Field>
              <Field
                label={"Last name"}
                isRequired
              >
                <input
                  type={"text"}
                  value={form.lastName}
                  onChange={update("lastName")}
                  required
                  maxLength={100}
                  autoComplete={"family-name"}
                />
              </Field>
              <Field
                label={"Email"}
                isRequired
              >
                <input
                  type={"email"}
                  value={form.email}
                  onChange={update("email")}
                  required
                  maxLength={200}
                  autoComplete={"email"}
                />
              </Field>
              <Field
                label={"Phone"}
                isRequired
              >
                <input
                  type={"tel"}
                  value={form.phone}
                  onChange={update("phone")}
                  required
                  maxLength={30}
                  autoComplete={"tel"}
                />
              </Field>
              <Field label={"Location"}>
                <input
                  type={"text"}
                  value={form.location}
                  onChange={update("location")}
                  maxLength={200}
                  autoComplete={"address-level2"}
                />
              </Field>
              <Field
                label={"Subject"}
                isRequired
              >
                <input
                  type={"text"}
                  value={form.subject}
                  onChange={update("subject")}
                  required
                  maxLength={200}
                />
              </Field>
              <Field
                label={"Budget Estimate"}
                isRequired
              >
                <input
                  type={"text"}
                  value={form.budgetEstimate}
                  onChange={update("budgetEstimate")}
                  required
                  maxLength={100}
                />
              </Field>

              <fieldset
                ref={areasRef}
                className={"contact-areas"}
              >
                <legend className={"contact-label"}>
                  Area<span aria-hidden={"true"}> *</span>
                </legend>
                <div className={"contact-areas-grid"}>
                  {CONTACT.AREAS.map((area) => (
                    <label
                      key={area}
                      className={"contact-check"}
                    >
                      <input
                        type={"checkbox"}
                        checked={form.areas.includes(area)}
                        onChange={toggleArea(area)}
                      />
                      <span>{area}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <Field label={"Message"}>
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  maxLength={5000}
                  rows={4}
                />
              </Field>
              <label
                className={"contact-honeypot"}
                aria-hidden={"true"}
              >
                Website
                <input
                  type={"text"}
                  value={form.website}
                  onChange={update("website")}
                  tabIndex={-1}
                  autoComplete={"off"}
                />
              </label>

              {errorText ? (
                <p
                  className={"contact-error"}
                  role={"alert"}
                >
                  {errorText}
                </p>
              ) : null}

              <button
                type={"submit"}
                className={"btn contact-submit"}
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Submit"}
              </button>
            </form>
          )}
        </div>

        <aside className={"contact-details"}>
          <h2 className={"contact-details-title"}>{CONTACT.HOURS_TITLE}</h2>
          <p className={"contact-details-text"}>
            {CONTACT.HOURS.map((line) => (
              <React.Fragment key={line}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </p>
          <p className={"contact-details-text"}>
            <a href={CONTACT_DETAILS.PHONE.link}>
              <strong>Call:</strong> {CONTACT_DETAILS.PHONE.label}
            </a>
            <br />
            <a
              href={CONTACT_DETAILS.MAP_LINK}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              {CONTACT_DETAILS.ADDRESS.join(" ")}
            </a>
          </p>
        </aside>
      </section>

      <Consultation />
    </React.Fragment>
  );
};

export default Contact;
